import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { 
  Briefcase, Users, Video, Target, Search, 
  ArrowRight, CheckCircle, Clock, TrendingUp,
  FileText, Award, Zap, Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsMarketing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-pink-900 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M0 0h40v40H0V0zm40 40h40v40H40V40z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 rounded-full text-white font-semibold mb-6 border border-white/30">
              <CheckCircle className="w-4 h-4" />
              <span>Now Live!</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-white drop-shadow-lg">
              Find Your Dream Job or Hire Top Talent
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-purple-100 max-w-3xl mx-auto drop-shadow">
              Australia's next-generation job marketplace with video applications and smart matching
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={() => navigate('/jobs/browse')}
                className="px-8 py-4 bg-white text-purple-600 font-semibold rounded-xl hover:bg-purple-50 transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2 text-lg"
              >
                <Search className="w-6 h-6" />
                Browse Jobs
              </button>
              
              {user ? (
                user.has_jobs_profile ? (
                  <button
                    onClick={() => navigate('/jobs/dashboard')}
                    className="px-8 py-4 bg-purple-500 text-white font-semibold rounded-xl hover:bg-purple-600 transition-all transform hover:scale-105 shadow-xl text-lg border-2 border-white/20"
                  >
                    Go to Dashboard
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/jobs/onboarding')}
                    className="px-8 py-4 bg-purple-500 text-white font-semibold rounded-xl hover:bg-purple-600 transition-all transform hover:scale-105 shadow-xl text-lg border-2 border-white/20"
                  >
                    Get Started
                  </button>
                )
              ) : (
                <button
                  onClick={() => navigate('/jobs/register')}
                  className="px-8 py-4 bg-purple-500 text-white font-semibold rounded-xl hover:bg-purple-600 transition-all transform hover:scale-105 shadow-xl text-lg border-2 border-white/20"
                >
                  Post a Job
                </button>
              )}
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-white">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <Briefcase className="w-5 h-5" />
                <span className="font-medium">1,000+ Jobs</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <Users className="w-5 h-5" />
                <span className="font-medium">10,000+ Candidates</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <Video className="w-5 h-5" />
                <span className="font-medium">Video Applications</span>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" className="fill-background"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">Innovative Job Search Tools</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Modern features that give you an edge
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-card rounded-2xl p-8 border-2 border-border hover:border-purple-300 dark:hover:border-purple-600 transition-colors shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Video className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Video Applications</h3>
              <p className="text-muted-foreground">
                Stand out with video cover letters. Show your personality and passion beyond your resume.
              </p>
            </div>
            
            <div className="bg-card rounded-2xl p-8 border-2 border-border hover:border-blue-300 dark:hover:border-blue-600 transition-colors shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Smart Matching</h3>
              <p className="text-muted-foreground">
                AI-powered job matching connects you with opportunities that truly fit your skills and goals.
              </p>
            </div>
            
            <div className="bg-card rounded-2xl p-8 border-2 border-border hover:border-pink-300 dark:hover:border-pink-600 transition-colors shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">One-Way Interviews</h3>
              <p className="text-muted-foreground">
                Record answers on your schedule. No back-and-forth scheduling needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-foreground">How GoJobs Works</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Find your next opportunity in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6 rounded-2xl bg-card hover:bg-muted/50 transition-colors border border-border">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                1
              </div>
              <div className="mb-4">
                <Search className="w-12 h-12 text-purple-600 mx-auto" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Search Jobs</h3>
              <p className="text-muted-foreground">
                Browse thousands of opportunities by role, location, and salary range
              </p>
            </div>

            <div className="text-center p-6 rounded-2xl bg-card hover:bg-muted/50 transition-colors border border-border">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                2
              </div>
              <div className="mb-4">
                <Video className="w-12 h-12 text-purple-600 mx-auto" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Apply with Video</h3>
              <p className="text-muted-foreground">
                Upload your resume and record a video cover letter to stand out
              </p>
            </div>

            <div className="text-center p-6 rounded-2xl bg-card hover:bg-muted/50 transition-colors border border-border">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                3
              </div>
              <div className="mb-4">
                <Award className="w-12 h-12 text-purple-600 mx-auto" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Get Hired</h3>
              <p className="text-muted-foreground">
                Interview with employers and land your dream job faster
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Job Seekers & Employers */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-foreground">For Job Seekers & Employers</h2>
              <p className="text-xl text-muted-foreground">Two powerful platforms in one</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Job Seekers */}
              <div className="bg-card rounded-2xl p-8 shadow-xl border-2 border-border hover:border-purple-300 dark:hover:border-purple-600 transition-colors">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">For Job Seekers</h3>
                </div>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Browse thousands of job opportunities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Create professional profile with resume</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Apply with video cover letters</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Track your applications</span>
                  </li>
                </ul>
                
                <button
                  onClick={() => user ? navigate('/jobs/onboarding?type=job_seeker') : navigate('/jobs/register')}
                  className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg"
                >
                  Find Jobs
                </button>
              </div>
              
              {/* Employers */}
              <div className="bg-card rounded-2xl p-8 shadow-xl border-2 border-border hover:border-blue-300 dark:hover:border-blue-600 transition-colors">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">For Employers</h3>
                </div>
                
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Post unlimited job listings</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Access qualified candidates instantly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Review video applications</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-card-foreground">Manage hiring pipeline</span>
                  </li>
                </ul>
                
                <button
                  onClick={() => user ? navigate('/jobs/onboarding?type=employer') : navigate('/jobs/register')}
                  className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg"
                >
                  Post Jobs
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-600 via-purple-700 to-pink-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white drop-shadow-lg">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-purple-100 mb-8 drop-shadow">
              Join thousands using GoJobs to advance their careers
            </p>
            
            <button
              onClick={() => navigate('/jobs/browse')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-purple-600 font-semibold rounded-xl hover:bg-purple-50 transition-all shadow-xl text-lg"
            >
              Browse Jobs Now
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobsMarketing;
